// src/features/records/components/GalleryView/GalleryView.js
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './GalleryView.module.css';
import JustifiedGrid from './components/JustifiedGrid';
import EmptyState from './components/EmptyState';
import SkeletonGrid from './components/SkeletonGrid';
import MediaLightbox from './components/MediaLightbox';
import { useMediaGallery } from './hooks/useMediaGallery';
import useRecordDetail from '../../hooks/useRecordDetail';
import { useRecordsLayoutContext } from '../../hooks/useRecordsLayoutContext';
import mapRecordsErrorToKey from '../../utils/recordsErrorMapper';

const GalleryView = () => {
  const { t } = useTranslation();
  const { searchQuery } = useRecordsLayoutContext();
  const { openDetail } = useRecordDetail();
  const { 
    media, 
    loading, 
    loadingMore, 
    error, 
    hasMore, 
    loadMore,
    refresh,
    initialized 
  } = useMediaGallery({ searchQuery });
  const galleryErrorKey = error
    ? mapRecordsErrorToKey(error, { fallbackKey: 'records.views.gallery.errors.load_failed' })
    : null;

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Handle media click - open lightbox
  const handleMediaClick = useCallback((clickedMedia) => {
    const index = media.findIndex(item => item.id === clickedMedia.id);
    if (index !== -1) {
      setSelectedIndex(index);
      setLightboxOpen(true);
    }
  }, [media]);

  // Handle opening record from lightbox
  const handleOpenRecord = useCallback((recordId) => {
    // Pass minimal record data - RecordDetail will fetch the full record
    openDetail({ id: recordId });
  }, [openDetail]);

  // Show skeleton while either data is loading or we haven't resolved the first page yet
  if (loading || !initialized) {
    return (
      <div className={styles.container}>
        <SkeletonGrid />
      </div>
    );
  }

  // Error state with retry capability
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <div className={styles.errorContent}>
            <p className={styles.errorText}>
              {t(galleryErrorKey || 'records.views.gallery.errors.load_failed')}
            </p>
            <button 
              className={styles.retryButton}
              onClick={refresh}
              type="button"
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (media.length === 0) {
    return (
      <div className={styles.container}>
        <EmptyState />
      </div>
    );
  }

  // Gallery grid
  return (
    <div className={styles.container}>
      <JustifiedGrid 
        media={media} 
        loadMore={loadMore}
        hasMore={hasMore}
        loadingMore={loadingMore}
        onMediaClick={handleMediaClick}
      />
      
      {/* Media Lightbox - conditionally mounted with key for fresh state */}
      {lightboxOpen && (
        <MediaLightbox
          key={`lb-${selectedIndex}`}
          items={media}
          initialIndex={selectedIndex}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          onOpenItem={handleOpenRecord}
        />
      )}
    </div>
  );
};

export default GalleryView;
